import { useEffect, useMemo, useState } from "react";
import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes";
import Accordion from "../../components/Accordion";
import { Container, Headline, MainContentWrapper, SalaryInsuranceInfo } from "./ProductStepV2.style";
import { useStore } from "../../../../store/store";
import ContactMeStep from "../ContactMeStep";
import Analytics from "../../../../utils/analytics";
import { TrackingFlowTypeEnum } from "../../../../types/TrackingFlowTypeEnum";
import { getCookie } from "../../../../utils/cookieService";
import ProductCardV2 from "../../components/ProductCardV2";

export default function ProductStep() {
  const payload = useStore.usePayload();
  const setPayload = useStore.useSetPayload();
  const setCompletedAccordionRoute = useStore.useSetCompletedAccordionRoute();
  const setAccordionRoute = useStore.useSetAccordionRoute();
  const accordionRoute = useStore.useAccordionRoute();
  const [showContactMe, setShowContactMe] = useState(false);
  const translations = useStore.useTranslations();
  const offerEnabled = useStore.useOfferEnabled();

  //Bloomreach cookie
  const exponeaCookie = () => {
    if (payload.cookie === undefined) {
      const cookie = getCookie("__exponea_etc__");
      if (cookie) {
        setPayload({ cookie: cookie });
      }
    } else {
      return;
    }
  };

  //Preeselcted products via productcard: If you go to OI via productcards, the choosen products is set in URL and they need to be preselected
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const product = urlParams.get("produkt");
    const campaign = urlParams.get("campaign");
    const productList = product?.split("|");

    if (product) {
      setPayload({
        wantsUnemploymentBenefits: false,
        wantsSalaryInsurance: false,
        wantsUnion: false,
        wantsHRJura: false,
        wantsBasic: false,
        wantsSalaryInsuranceAndUnion: false,
        source: campaign ? campaign : undefined
      });

      if (productList?.includes("akasse")) {
        setPayload({
          wantsUnemploymentBenefits: true,
        });
      }

      if (productList?.includes("fagforening") && payload.occupation !== "SelfEmployed") {
        setPayload({
          wantsUnion: true,
          wantsSalaryInsuranceAndUnion: false
        });
      }

      if (productList?.includes("hrjura") && payload.occupation === "SelfEmployed") {
        setPayload({
          wantsHRJura: true,
        });
      }

      if (productList?.includes("loensikring") && payload.occupation !== "Unemployed") {
        setPayload({
          wantsSalaryInsurance: true,
        });
      }

      if (productList?.includes("basis") && payload.occupation === "SelfEmployed") {
        setPayload({
          wantsBasic: true,
        });
      }

      if (productList?.includes("fagforeningloensikring") && (payload.occupation === "Employee")) {
        setPayload({
          wantsSalaryInsuranceAndUnion: true,
          wantsUnion: false,
          wantsUnemploymentBenefits: true,
          wantsSalaryInsurance: false,
        });
      }

    }
  }, [payload.occupation]);
  //Preslected products end

  //Employee without unemployment insurance in advance loses access to salary insurance, same as Unemployed occupation
  useEffect(() => {
    if (payload.occupation === "Employee" && payload.hasUnemploymentInsuranceInAdvance === false && (payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion)) {
      setPayload({ wantsSalaryInsurance: false, wantsSalaryInsuranceAndUnion: false });
    }
  }, [payload.occupation, payload.hasUnemploymentInsuranceInAdvance]);

  //SelfEmployed should go straight to ContactMeStep, before choosing products
  useEffect(() => {
    if (payload.occupation === "SelfEmployed") {
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, TrackingFlowTypeEnum.VerticalV2);
      setShowContactMe(true);
    }
  }, [payload.occupation]);

  //SelfEmployed should always be forced back into ContactMeStep, even after closing it
  useEffect(() => {
    if (payload.occupation === "SelfEmployed" && accordionRoute === AccordionRoutesEnum.ProductStepV2) {
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, TrackingFlowTypeEnum.VerticalV2);
      setShowContactMe(true);
    }
  }, [accordionRoute]);

  const setDiscount = () => {
    if (payload.wantsUnemploymentBenefits && payload.wantsUnion && offerEnabled) {
      setPayload({ combinationDiscount: true });
    } else {
      setPayload({ combinationDiscount: false });
    }
  };

  const unemploymentBenefitsCheck = () => {
    if (payload.wantsUnemploymentBenefits && payload.wantsSalaryInsuranceAndUnion) {
      setPayload({ wantsUnemploymentBenefits: true, wantsSalaryInsuranceAndUnion: false });
    } else if (payload.wantsUnemploymentBenefits) {
      setPayload({ wantsUnemploymentBenefits: false });
    } else {
      setPayload({ wantsUnemploymentBenefits: true, wantsSalaryInsuranceAndUnion: false });
    }
  }

  const unemploymentBenefitsPrice = () => {
    if (payload.occupation === "SelfEmployed") {
      return `${translations?.productsStep.unemploymentInsurance.price_selfemployed}`;
    } else if (payload.occupation === "Student") {
      return `0`;
    } else {
      return `${translations?.productsStep.unemploymentInsurance.price}`;
    }
  }

  const unemploymentBenefitsOfferText = () => {
    if (payload.occupation === "SelfEmployed") {
      return undefined;
    } else {
      return !payload.wantsUnion ? "Tilføj fagforening og spar 444 kr." : undefined;
    }
  }

  //Without existing unemployment insurance in advance (answered in OccupationStep), salary insurance is unavailable, same as for the Unemployed occupation
  const salaryProductsUnavailable = payload.occupation === "Unemployed" || (payload.occupation === "Employee" && payload.hasUnemploymentInsuranceInAdvance === false);

  const nextStepAllowed = useMemo(() => {
    if ((payload.occupation === "Employee" || payload.occupation === "Unemployed") && (payload.wantsUnemploymentBenefits || payload.wantsSalaryInsurance || payload.wantsUnion || payload.wantsSalaryInsuranceAndUnion)) {
      return true;
    } else if (payload.occupation === "SelfEmployed") {
      if (payload.wantsUnemploymentBenefits || payload.wantsSalaryInsurance || payload.wantsBasic || payload.wantsHRJura) {
        return true;
      }
      if (payload.wantsUnemploymentBenefits || payload.wantsSalaryInsurance || payload.wantsBasic || payload.wantsHRJura) {
        return true;
      }
      return false;
    } else if (payload.occupation === "Student" && (payload.wantsUnemploymentBenefits || payload.wantsUnion)) {
      return true;
    } else {
      return false;
    }
  }, [payload]);

  //Tracking
  //Tracking of questions showed on next step - circumstanceStep
  const circumstanceTracking = () => {
    const questions = [];

    questions.push("Arbejder du primært i DK");

    // needs attention for tracking questions related to unemployment benefits
    if (payload.wantsUnemploymentBenefits) {
      questions.push("Er du medlem af en akasse");
      
    }

    if (payload.wantsUnion) {
      questions.push("Er du medlem af en fagforening i dag");
    }

    if (payload.wantsSalaryInsuranceAndUnion) {
      questions.push("Er du medlem af en fagforening i dag");
      questions.push("Er du medlem af en akasse");
    }

    if (payload.wantsHRJura || payload.wantsBasic) {
      questions.push("Er du selvstændig på fuldtid");
    }

    // Analytics.getInstance().trackCircumstanceStep(questions, "circumstance_questions_start");
  };

  const next = () => {
    setPayload({ cause: "" });
    if ((payload.occupation === "Employee" || payload.occupation === "Unemployed") && payload.wantsSalaryInsuranceAndUnion) {
      setPayload({ cause: "Ønsker fagforening + lønsikring" });
    }
    if (payload.occupation === "SelfEmployed" && payload.doYouRunYourOwnBusiness === false) {
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, TrackingFlowTypeEnum.VerticalV2);
      setShowContactMe(true);
    } else if ((payload.occupation === "Employee" || payload.occupation === "Unemployed") && (payload.wantsSalaryInsurance) && !payload.wantsUnemploymentBenefits && !payload.wantsUnion) {
      setPayload({ cause: "Kun lønsikring" });
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, TrackingFlowTypeEnum.VerticalV2);
      setShowContactMe(true);
    } else if (payload.occupation === "SelfEmployed" && ((payload.wantsSalaryInsurance && !payload.wantsUnemploymentBenefits) || ((payload.wantsHRJura || payload.wantsBasic) && !payload.wantsUnemploymentBenefits))) {
      if (payload.wantsBasic) {
        setPayload({ cause: "Ønsker selvstændig forening" });
      }
      if (payload.wantsHRJura) {
        setPayload({ cause: "Ønsker Hr jura" });
      }
      if (payload.wantsSalaryInsurance) {
        setPayload({ cause: "Ønsker Lønsikring" });
      }
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, TrackingFlowTypeEnum.VerticalV2);
      setShowContactMe(true);
    } else if (payload.occupation === "Student" && payload.wantsUnemploymentBenefits) {
      setPayload({ cause: "Student" });
      setAccordionRoute(AccordionRoutesEnum.ContactMeStep, payload, TrackingFlowTypeEnum.VerticalV2);
      setShowContactMe(true);
    } else {
      setAccordionRoute(AccordionRoutesEnum.CircumstanceStep, payload, TrackingFlowTypeEnum.VerticalV2);
      circumstanceTracking();
    }
  };

  const back = () => {
    setAccordionRoute(AccordionRoutesEnum.OccupationStep, payload, TrackingFlowTypeEnum.VerticalV2);
    setShowContactMe(false);
  };

//Trigger the function of onClick when pressing enter or space on a card
  const keyDownUnemploymentBenefits = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        payload.wantsUnemploymentBenefits ? setPayload({ wantsUnemploymentBenefits: false, wantsSalaryInsuranceAndUnion: false }) : setPayload({ wantsUnemploymentBenefits: true });
      } 
    } 
  } 

  const keyDownSalaryInsuranceSelf = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        payload.wantsSalaryInsurance ? setPayload({ wantsSalaryInsurance: false }) : setPayload({ wantsSalaryInsurance: true });
      } 
    } 
  } 

  const keyDownUnion = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        setPayload({ wantsUnion: !payload.wantsUnion, wantsFamilyUnion: false, wantsSalaryInsuranceAndUnion: false });
      } 
    } 
  } 

  const keyDownUnionStudent = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        setPayload({ wantsUnion: !payload.wantsUnion });
      } 
    } 
  } 

  const keyDownSalaryInsurance = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        payload.wantsSalaryInsurance ? setPayload({ wantsSalaryInsurance: false }) : setPayload({ wantsSalaryInsurance: true, wantsSalaryInsuranceAndUnion: false })
      } 
    } 
  }

  const keyDownSalaryInsuranceAndUnion = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        payload.wantsSalaryInsuranceAndUnion ? setPayload({ wantsSalaryInsuranceAndUnion: false }) : setPayload({ wantsSalaryInsuranceAndUnion: true, wantsSalaryInsurance: false, wantsUnion: false });
      } 
    } 
  } 

  const keyDownBasic = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        payload.wantsBasic ? setPayload({ wantsBasic: false }) : setPayload({ wantsBasic: true });
      } 
    } 
  }

  const keyDownHrJura = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event) {
      if(event && (event.key === 'Enter' || event.key === ' ')) {
        payload.wantsHRJura ? setPayload({ wantsHRJura: false }) : setPayload({ wantsHRJura: true });
      } 
    } 
  }
//Trigger the function of onClick when pressing enter or space on a card end

  return (
    <>
      <Accordion
        title={translations?.productsStep.title}
        step={AccordionRoutesEnum.ProductStepV2}
        onNext={() => {
          // Add tracking
          // Analytics.getInstance().trackProducts(payload, offerEnabled, translations, TrackingFlowTypeEnum.VerticalV2);
          setCompletedAccordionRoute(AccordionRoutesEnum.ProductStepV2);
          setDiscount();
          next();
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          exponeaCookie();
        }}
        stepComplete={nextStepAllowed}
        previousStep={AccordionRoutesEnum.OccupationStep}
        headerImg="/assets/images/tdesign_member.svg"
        step2={AccordionRoutesEnum.ProductStepV2}
      >
        <MainContentWrapper>
          {payload.occupation == "SelfEmployed" && <Headline>{translations?.productsStep.description}</Headline>}
         
            <Container>
              {payload.occupation === "Employee" && !salaryProductsUnavailable && (
              <ProductCardV2 
                title={translations?.productsStep.salaryInsuranceAndUnion.title} 
                price={`${translations?.productsStep.salaryInsuranceAndUnion.price}`} 
                onClick={() => (payload.wantsSalaryInsuranceAndUnion ? setPayload({ wantsSalaryInsuranceAndUnion: false, wantsUnemploymentBenefits: false }) : setPayload({ wantsSalaryInsuranceAndUnion: true, wantsSalaryInsurance: false, wantsUnion: false, wantsUnemploymentBenefits: true }))} 
                isChecked={payload.wantsSalaryInsuranceAndUnion === true}
                offerText={translations?.productsStep.salaryInsuranceAndUnion.offerText}
                onKeyDown={(event) => keyDownSalaryInsuranceAndUnion(event)}
               /*  htmlContent={
                  !translations ? "" : translations?.productsStep.salaryInsuranceAndUnion.description
                } */
               info={[{description: "Dagpenge, hvis du mister jobbet"}, {description: "Fagforening med juridisk tryghed"}, {description: "Lønsikring op til 6.000 kr./md. ekstra"}]}
              />
            )}
              {payload.occupation !== "Student" && (
              <ProductCardV2 
                title={translations?.productsStep.unemploymentInsurance.title} 
                price={unemploymentBenefitsPrice()} 
                onClick={() => unemploymentBenefitsCheck()} 
                isChecked={payload.wantsUnemploymentBenefits === true && (payload.wantsSalaryInsuranceAndUnion === false || payload.wantsSalaryInsuranceAndUnion === undefined)} 
                offerText={translations?.productsStep.unemploymentInsurance.offerText}
                onKeyDown={(event) => keyDownUnemploymentBenefits(event)}
                bottomOfferText={unemploymentBenefitsOfferText()}
              /*   htmlContent={
                  !translations ? "" 
                  : payload.occupation === "Student" ? translations?.productsStep.unemploymentInsurance.description_student 
                  : translations?.productsStep.unemploymentInsurance.description} */
                  info={[{description: "Økonomisk tryghed, hvis du mister jobbet"}, {description: "Få op til 25.070 kr./md. i dagpenge"}]}
              />
              )}
              {payload.occupation === "Student" && (
              <ProductCardV2 
                title={translations?.productsStep.unemploymentInsurance.title} 
                price={unemploymentBenefitsPrice()} 
                onClick={() => unemploymentBenefitsCheck()} 
                isChecked={payload.wantsUnemploymentBenefits === true && (payload.wantsSalaryInsuranceAndUnion === false || payload.wantsSalaryInsuranceAndUnion === undefined)} 
                offerText={translations?.productsStep.unemploymentInsurance.offerText}
                onKeyDown={(event) => keyDownUnemploymentBenefits(event)}
                bottomOfferText={undefined}
              /*   htmlContent={
                  !translations ? "" 
                  : payload.occupation === "Student" ? translations?.productsStep.unemploymentInsurance.description_student 
                  : translations?.productsStep.unemploymentInsurance.description} */
                  info={[{description: "Økonomisk tryghed, hvis du mister jobbet"}, {description: "Få op til 17.295 kr./md. i dagpenge"}]}
              />
              )}
              {payload.occupation === "SelfEmployed" && (
                <ProductCardV2 
                  title={translations?.productsStep.salaryGuarantee.title_selfemployed}
                  price={`Fra ${translations?.productsStep.salaryGuarantee.price}`}
                  onClick={() => (payload.wantsSalaryInsurance ? setPayload({ wantsSalaryInsurance: false }) : setPayload({ wantsSalaryInsurance: true }))} 
                  isChecked={payload.wantsSalaryInsurance === true} 
                  offerText={translations?.productsStep.salaryGuarantee.offerText}
                  onKeyDown={(event) => keyDownSalaryInsuranceSelf(event)}
                  removekrPerMonth={false}
                 /*  htmlContent={
                    !translations ? "" : translations?.productsStep.salaryGuarantee.description_selfemployed
                  } */
                 bottomOfferText={undefined}
                 info={[{description: "Få fra 2.000 til 30.000 kr./md. oveni dagpengene"}, {description: "Vi ringer til dig, så vi sammen kan finde den bedste løsning til dig"}]}
                />
              )}
              {(payload.occupation === "Employee" || payload.occupation === "Unemployed") && (
              <ProductCardV2
                title={translations?.productsStep.union.title}
                price={payload.wantsUnemploymentBenefits && !payload.wantsSalaryInsuranceAndUnion ? `Gratis i 6 mdr.` : `${translations?.productsStep.union.price}`}
                extraPrice={payload.wantsUnemploymentBenefits && !payload.wantsSalaryInsuranceAndUnion ? `Herefter 74 kr./md.` : ""}
                onClick={() => {
                  setPayload({ wantsUnion: !payload.wantsUnion, wantsSalaryInsuranceAndUnion: false });
                }}
                isChecked={payload.wantsUnion === true}
                hasOffer={offerEnabled === true ? true : false}
                offerText="Gratis fagforening i 6 mdr. med a-kassen"
               /*  combinationDiscount={payload.wantsUnemploymentBenefits && payload.wantsUnion && offerEnabled} */
                onKeyDown={(event) => {keyDownUnion(event)}}
               /*  htmlContent={
                  !translations ? "" : translations?.productsStep.union.description
                } */
                removekrPerMonth={payload.wantsUnemploymentBenefits && !payload.wantsSalaryInsuranceAndUnion}
                bottomOfferText={!payload.wantsUnemploymentBenefits ? "Tilføj a-kasse og spar 444 kr." : "Du får gratis fagforening i 6 mdr."}
                info={[{description: "Rådgivning om job og karriere og juridisk hjælp ved problemer på arbejdspladsen"}, {description: "Dækker også dine børn op til 21 år"}]}
              />
              )}
              {payload.occupation === "Student" && (
              <ProductCardV2
                title={translations?.productsStep.union.title}
                price={`${translations?.productsStep.union.price}`}
                onClick={() => {
                  setPayload({ wantsUnion: !payload.wantsUnion });
                }}
                isChecked={payload.wantsUnion === true}
                hasOffer={false}
                offerText="Gratis fagforening i 6 mdr. med a-kassen"
                onKeyDown={(event) => {keyDownUnionStudent(event)}}
                //opacity={payload.wantsFamilyUnion === true ? true : false}
                /* htmlContent={
                  !translations ? "" : translations?.productsStep.union.description_student
                } */
               info={[{description: "Rådgivning om job og karriere"}, {description: "Juridisk hjælp ved problemer på studiejobbet"}]}
              />
            )}
           
        {/* <MainContentWrapper> */}
        {/* {payload.occupation !== "Student" && <Headline>{payload.occupation === "Employee" || payload.occupation === "Unemployed" ? "Vælg en Fagforening" : "Til din virksomhed"}</Headline>} */}
         {/* product is available for employeed */}
            {(payload.occupation === "Employee") && !salaryProductsUnavailable && (
              <ProductCardV2 
                title={translations?.productsStep.salaryGuarantee.title} 
                price={`Fra ${translations?.productsStep.salaryGuarantee.price}`}
                onClick={() => (payload.wantsSalaryInsurance ? setPayload({ wantsSalaryInsurance: false }) : setPayload({ wantsSalaryInsurance: true, wantsSalaryInsuranceAndUnion: false }))} 
                isChecked={payload.wantsSalaryInsurance === true} 
                onKeyDown={(event) => keyDownSalaryInsurance(event)}
                removekrPerMonth={false}
                /* htmlContent={
                  !translations ? "" : translations?.productsStep.salaryGuarantee.description
                } */
               info={[{description: "Få fra 2.000 til 30.000 kr./md. oveni dagpengene"}, {description: "Vi ringer til dig, så vi sammen kan finde den bedste løsning til dig"}]}
              />
            )}
            {/* product is not available for unemployed, or for employees without unemployment insurance in advance */}
             {salaryProductsUnavailable && (
              <ProductCardV2 
                title={translations?.productsStep.salaryGuarantee.title} 
                price={`Ikke tilgængelig`}
                onClick={() => {}}
                isChecked={payload.wantsSalaryInsurance === true} 
                onKeyDown={() => {}}
                removekrPerMonth={true}
                priceUnavailable={true}
                /* htmlContent={
                  !translations ? "" : translations?.productsStep.salaryGuarantee.description
                } */
               info={[{description: `Vi kan på nuværende tidspunkt ikke tilbyde dig ${translations?.productsStep.salaryGuarantee.title}`}]}
              />
            )}
            {salaryProductsUnavailable && (
              <ProductCardV2 
                title={translations?.productsStep.salaryInsuranceAndUnion.title} 
                price={`Ikke tilgængelig`} 
                onClick={() => {}} 
                isChecked={payload.wantsSalaryInsuranceAndUnion === true}
                offerText={translations?.productsStep.salaryInsuranceAndUnion.offerText}
                onKeyDown={() => {}}
                removekrPerMonth={true}
                priceUnavailable={true}
               /*  htmlContent={
                  !translations ? "" : translations?.productsStep.salaryInsuranceAndUnion.description
                } */
               info={[{description: `Vi kan på nuværende tidspunkt ikke tilbyde dig ${translations?.productsStep.salaryInsuranceAndUnion.title}`}]}
              />
            )}
            {payload.occupation === "SelfEmployed" && (
              <>
                <ProductCardV2
                  title="Ase Selvstændig"
                  price={payload.wantsUnemploymentBenefits ? `${translations?.productsStep.basic.price}` : `149`}
                  onClick={() => {
                    payload.wantsBasic ? setPayload({ wantsBasic: false }) : setPayload({ wantsBasic: true });
                  }}
                  isChecked={payload.wantsBasic === true}
                  offerText={translations?.productsStep.basic.offerText}
                  onKeyDown={(event) => {keyDownBasic(event)}}
                  /* htmlContent={
                    !translations ? "" : translations?.productsStep.basic.description
                  } */
                 info={[{description: "Erhvervshotline - personlig sparring til din virksomhed"}, {description: "Adgang til juridiske skabeloner, dokumenter og kontrakter"}]}
                />
                <ProductCardV2
                  title={translations?.productsStep.hrjura.title}
                  price={`Fra ${translations?.productsStep.hrjura.price}`}
                  onClick={() => {
                    payload.wantsHRJura ? setPayload({ wantsHRJura: false }) : setPayload({ wantsHRJura: true });
                  }}
                  isChecked={payload.wantsHRJura === true}
                  offerText={translations?.productsStep.hrjura.offerText}
                  onKeyDown={(event) => {keyDownHrJura(event)}}
                 /*  htmlContent={
                    !translations ? "" : translations?.productsStep.hrjura.description
                  } */
                 info={[{description: "Undgå dyre fejl i f.eks. kontrakter og ansættelsesrettigheder"}, {description: "Sparring fra erfarne HR-konsulenter"}]}
                />
              </>
            )}
             </Container>
          
        </MainContentWrapper> 
          
          {payload.wantsSalaryInsuranceAndUnion && (
              <SalaryInsuranceInfo dangerouslySetInnerHTML={{ __html: ` ${!translations ? "" : translations?.productsStep.salaryInsuranceAndUnion.information}`}} />
            )}
            {payload.wantsSalaryInsurance && (
              <SalaryInsuranceInfo dangerouslySetInnerHTML={{ __html: ` ${!translations ? "" : translations?.productsStep.salaryGuarantee.information}`}} />
            )}
        {/* </MainContentWrapper> */}
        {/* <RecieptContainer>
          <Reciept basePrice={true} />
        </RecieptContainer> */}
      </Accordion>

      <ContactMeStep showContactMe={showContactMe} onClose={back} />
    </>
  );
}
